import { Typography } from "@mui/material";
import { ReactComponent as AlertTriangleIcon } from "components/Icons/alert-triangle.svg";
import {
  CellAlign,
  OLTable,
  Row,
  createHeadItems,
} from "components/Table/OLTable";
import { ExternalLinkTooltip } from "components/Tooltips/ExternalLinkTooltip";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import { useCallback } from "react";
import { Link } from "react-router-dom";
import { CUSTOM_EVENT_SCHEMA_UNSUCCESS_ID } from "state/tables/types";
import { links } from "utilities/links";
import { AttributeChip } from "../SchemaList/AttributeChips";
import { UnsuccessfulCustomEventSchema } from "./CustomEventSchemaImportList";
import { useStyles } from "./CustomEventSchemaImportList.styles";
import { isCustomEventSchemaJsonValid } from "./utils";

interface Props {
  customEventSchemas: UnsuccessfulCustomEventSchema[];
  disableTitle?: boolean;
  importType?: "global";
}

export const UnsuccessfulImports = ({
  customEventSchemas,
  disableTitle,
  importType,
}: Props) => {
  const { message } = useMessage();
  const classes = useStyles();

  const headItems = createHeadItems([
    {
      headerText: message({ id: "eventName" }),
      property: "name",
      isSortable: false,
    },
    {
      headerText: message({ id: "eventSystemIdentifier" }),
      property: "eventType",
      isSortable: false,
    },
    {
      headerText: message({ id: "attributes" }),
      property: "attributes",
      isSortable: false,
    },
    {
      headerText: "",
      property: "view",
      isSortable: false,
      customWidth: "100px",
    },
  ]);

  const createItems = useCallback(
    (items: UnsuccessfulCustomEventSchema[]) => {
      return items.map((item) => {
        const areUnsupportedFields =
          item.errors &&
          item.errors.some(
            (err) => err.code === "This form should not contain extra fields.",
          );
        const isJsonValid = isCustomEventSchemaJsonValid(item.data);
        const isEditPossible = !areUnsupportedFields && isJsonValid;

        return {
          cells: [
            {
              content: (
                <span className={classes.nameTdWrapper}>
                  <AlertTriangleIcon
                    className={`${classes.warnIcon} ${
                      !isEditPossible ? classes.errorIcon : ""
                    }`}
                  />
                  <Tooltip text={item.data.name} placement="bottom-end">
                    <span className={classes.name}>{item.data.name}</span>
                  </Tooltip>
                  {!isEditPossible && (
                    <ExternalLinkTooltip
                      externalLink="https://help.openloyalty.io/main-features/imports-exports/imports"
                      name={message({ id: "weDetectedUnsupportedFields" })}
                      text={message({
                        id: "fileHasUnupportedFieldsAndCouldNotBeImported",
                      })}
                    />
                  )}
                </span>
              ),
              nonClickable: true,
              property: "name",
            },
            {
              content: item.data.eventType,
              property: "eventType",
            },
            {
              content: item.data.schema?.fields?.length ? (
                <AttributeChip
                  items={item.data.schema.fields}
                  key={item.data.name}
                />
              ) : (
                "-"
              ),
              property: "attributes",
            },
            {
              content: (
                <div className={classes.linkWrapper}>
                  {isEditPossible && (
                    <Link
                      className={classes.link}
                      to={links.editImportedCustomEventSchema}
                      state={{
                        customEventSchemaData: item.data,
                        errors: item.errors,
                        importedId: item.importedId,
                        importType: importType || "single",
                      }}
                    >
                      {message({ id: "edit" })}
                    </Link>
                  )}
                </div>
              ),
              property: "view",
              align: CellAlign.CENTER,
            },
          ],
          id: item.importedId.toString(),
        };
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customEventSchemas],
  );

  return (
    <>
      {!disableTitle && (
        <Typography className={classes.tableTitle} mt={8}>
          {message({ id: "actionRequired" })}
        </Typography>
      )}
      <OLTable
        disableColumnManager
        tableName={CUSTOM_EVENT_SCHEMA_UNSUCCESS_ID}
        onSort={() => {}}
        columns={headItems}
        rows={createItems(customEventSchemas) as Row[]}
        sort={{ order: "asc", property: "", rowsPerPage: 10, page: 1 }}
        disableActions
      />
    </>
  );
};
